import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import ClientSideTable from '@/components/Table/ClientSideTable'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { offerGCPRGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerGCPREditRight'
import { formatOffer } from '@/features/offers/helpers/formatOffer'
import useOffer from '@/features/offers/hooks/useOffer'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'
import { findInviteeSlots } from '@/features/offers/offer-detail/helper/approverSlotMatching.helper'
import { APPROVAL_TABLE_TYPE } from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { authSelector } from '@/stores/auth/authSlice'
import { setApprovalInvitedUsers, showApprovalRemarkModal } from '@/stores/offers/offersSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { useExchangeRate } from '@/utils/exchangeRate.helper'
import { isNullish } from '@/utils/helper'
import { Box, Flex } from '@chakra-ui/react'
import {
  GCPRStatus,
  OfferApprovalType,
  OfferBookingStatus,
  PRApprovalStatus,
  PRApprovalType,
} from '@eapproval/offer-shared'
import moment from 'moment'
import { useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { calculateGCPRSubmissionTotalAmount, normalizeGCPRSubmissionForApi } from '../../utils/GCPR.helper'
import GCPRSubmissionActionButtons from './components/GCPRSubmissionActionButtons'
import GCPRSubmissionFormFields from './components/GCPRSubmissionFormFields'
import GCPRSubmissionHeader from './components/GCPRSubmissionHeader'
import GCPRApproverTable from './GCPRApproverTable'
import useGCPRSubmissionColumns from './hooks/useGCPRSubmissionColumns'

const GCPRSubmission = ({ submissionIndex, handleDeleteDraft }) => {
  const submissionDetail = useWatch({
    name: `sourcing.GCMaterialSubmissions.${submissionIndex}`,
  })
  const queryClient = useQueryClient()

  const { user } = useSelector(authSelector)
  const { data: exchangeRateMap } = useExchangeRate()

  const offerId = useWatch({ name: '_id' })

  const offerPermissions = useSelector(selectOfferPermissions)

  const canEditGCPRSubmission =
    offerPermissions[offerGCPRGroupPolicyType.GCPR_PR_SUBMISSION]?.edit &&
    submissionDetail?.status !== GCPRStatus.ARCHIVE

  const canEditLogistic =
    offerPermissions[offerGCPRGroupPolicyType.GCPR_EDIT_LOGISTIC]?.edit &&
    submissionDetail?.status !== GCPRStatus.ARCHIVE

  const canDoGCPRApproval =
    (offerPermissions[offerGCPRGroupPolicyType.GCPR_APPROVAL]?.edit ?? false) &&
    submissionDetail?.status !== GCPRStatus.ARCHIVE

  const canReplyGCPRInvitation =
    (offerPermissions[offerGCPRGroupPolicyType.GCPR_REPLY_INVITATION]?.edit ?? false) &&
    submissionDetail?.status !== GCPRStatus.ARCHIVE

  const miniumDeliveryDate = moment(submissionDetail?.requestDate).add(14, 'days').toDate()

  // Some GCPR approver types are assigned by contributor group rather than by user,
  // so matching "is this my approver slot" needs the same per-type switch as
  // submissionContainActionRequiredApproverDetail used to inline - not the generic
  // user/delegatee check used for Quotation/Booking/PR.
  const actionRequiredApproverSlots = (
    Array.isArray(submissionDetail.approverDetails) ? submissionDetail.approverDetails : []
  ).reduce((slots, detail, approverIndex) => {
    const isActionRequiredStatus = detail.status === PRApprovalStatus.REQUIRED
    let hasRightToApprove = false

    switch (detail.type) {
      case PRApprovalType.LICENSE_MANAGER:
        hasRightToApprove = user?.contributorGroup === ContributorGroupType.LICENSE_MANAGER
        break
      case PRApprovalType.LOGISTIC:
        hasRightToApprove = user.contributorGroup === ContributorGroupType.LOGISTIC
        break
      case PRApprovalType.COMMERCIAL_PROJECT_MANAGER:
        hasRightToApprove = user.contributorGroup === ContributorGroupType.COMMERCIAL_PROJECT_MANAGER
        break
      default:
        hasRightToApprove =
          detail?.user?._id === user?._id ||
          detail?.user === user?._id ||
          detail?.delegationDetail?.delegatee?._id === user?._id ||
          detail?.delegationDetail?.delegatee === user?._id
        break
    }

    if (isActionRequiredStatus && hasRightToApprove) {
      slots.push({ approverIndex, detail })
    }

    return slots
  }, [])

  const submissionContainActionRequiredApproverDetail = actionRequiredApproverSlots.length > 0
  const inviteSlots = actionRequiredApproverSlots
  const replySlots = findInviteeSlots(submissionDetail?.approverDetails, user?._id)

  const { getValues, setValue } = useFormContext()
  const handleDeleteRow = (rowIndex) => {
    const currentDetails = getValues(`sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails`) ?? []
    if (currentDetails.length <= 1) {
      toast.error('Cannot delete the last row')
      return
    }
    const updated = currentDetails.filter((_, i) => i !== rowIndex)
    setValue(`sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails`, updated, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const handleUpdateTableData = async (rowIndex, columnId, value) => {
    const base = `sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails.${rowIndex}`
    if (columnId === 'quantity') {
      setValue(`${base}.newRequestedQuantity`, value ?? 0, { shouldDirty: true, shouldTouch: true })
      setValue(`${base}.quantity`, value ?? 0, { shouldDirty: true, shouldTouch: true })
    } else {
      setValue(`${base}.${columnId}`, value, { shouldDirty: true, shouldTouch: true })
    }

    const requestedDetails = getValues(`sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails`) ?? []

    const updatedTotalAmount = calculateGCPRSubmissionTotalAmount(requestedDetails, exchangeRateMap)

    setValue(`sourcing.GCMaterialSubmissions.${submissionIndex}.totalAmount`, updatedTotalAmount, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }
  const dispatch = useDispatch()

  const { useUpdateOffer } = useOffer()
  const { useSubmitGCMaterialPR, useUpdateGCMaterialPR, useResubmitGCMaterialPR, useArchiveGCMaterialPR } =
    useOfferGCPR()

  const submitGCMaterialPR = useSubmitGCMaterialPR()
  const updateGCMaterialPR = useUpdateGCMaterialPR()
  const resubmitGCMaterialPR = useResubmitGCMaterialPR()
  const archiveGCMaterialPR = useArchiveGCMaterialPR()

  const updateOffer = useUpdateOffer()

  const PRSubmissionTableData = useMemo(() => {
    if (!Array.isArray(submissionDetail?.requestedDetails)) {
      return []
    }

    return submissionDetail.requestedDetails.map((row) => {
      const detail = row?.freezedDetail || row?.detail
      const unitCost = detail?.unitCost ? parseFloat(detail.unitCost) : (row?.manualUnitCost ?? 0)

      return {
        detail: detail,
        modelNumber: row.modelNumber,
        description: detail?.description || row?.description,
        SAPMaterialNumber: detail?.SAPMaterialNumber || '',
        currency: detail?.currency || row?.currency || 'HKD',
        quantity: row.quantity,
        unit: detail?.unit || row?.unit,
        unitCost: unitCost,
        manualUnitCostHKD: row?.manualUnitCostHKD,
        totalPrice: unitCost * (row.newRequestedQuantity || 0),
        license: detail?.license,
        hasCSID: detail?.CSID === true,
        hasZUROA: detail?.zuroa === true,
        hasBuildingX: detail?.isBuildingX === true,

        purchaseScenario: row?.purchaseScenario,
        startDate: row.startDate,
        subscriptionId: row.subscriptionId,
        trialEnd: row.trialEnd,
        contractEnd: row.contractEnd,
        subSystemName: row.subSystemName,
        vendor: detail?.vendor,
        PONumber: row.PONumber,
      }
    })
  }, [submissionDetail?.requestedDetails])

  const showSubscriptionFields = useMemo(() => {
    if (!Array.isArray(PRSubmissionTableData)) {
      return false
    }

    return PRSubmissionTableData.some((row) => row.hasZUROA || row.hasCSID || row.hasBuildingX)
  }, [PRSubmissionTableData])

  const showCSIDFields = useMemo(() => {
    if (!Array.isArray(PRSubmissionTableData)) return false
    return PRSubmissionTableData.some((row) => row.hasCSID)
  }, [PRSubmissionTableData])

  const columnDef = useGCPRSubmissionColumns({
    canEditGCPRSubmission,
    canEditLogistic,
    showSubscriptionFields,
    handleDeleteRow,
  })

  const handleSubmitGCMaterialPR = async () => {
    try {
      const currentOffer = getValues()

      if (
        currentOffer.status !== OfferBookingStatus.BOOKING_SUBMITTED &&
        currentOffer.status !== OfferBookingStatus.BOOKING_COMPLETED
      ) {
        toast.error('Offer must be in Booking Submitted or Booking Completed status before submitting PR')
        return
      }
      //Save Offer First
      const formattedOffer = formatOffer(currentOffer)

      dispatch(showLoadingOverlay())

      await updateOffer.mutateAsync({
        id: currentOffer._id,
        data: formattedOffer,
      })

      // return

      const res = await submitGCMaterialPR.mutateAsync({
        offerId: currentOffer._id,
        submissionIndex: submissionIndex,
      })
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleApproveGCMaterialPR = async () => {
    if (canEditLogistic) {
      const unmappedRows = (submissionDetail?.requestedDetails ?? []).filter(
        (row) => !row?.freezedDetail && !row?.detail
      )
      if (unmappedRows.length > 0) {
        const modelNumbers = unmappedRows.map((r) => r.modelNumber ?? '(unknown)').join(', ')
        toast.error(`The following items have no mapped GC Material detail: ${modelNumbers}`)
        return
      }

      const missingSAPRows = (submissionDetail?.requestedDetails ?? []).filter((row) => {
        const sapNumber =
          row?.freezedDetail?.SAPMaterialNumber || row?.detail?.SAPMaterialNumber || row?.SAPMaterialNumber
        return !sapNumber || sapNumber.trim() === ''
      })
      if (missingSAPRows.length > 0) {
        const modelNumbers = missingSAPRows.map((r) => r.modelNumber ?? '(unknown)').join(', ')
        toast.error(`The following items have no SAP Material Number: ${modelNumbers}`)
        return
      }
    }

    const isCPMApprover = user?.contributorGroup === ContributorGroupType.COMMERCIAL_PROJECT_MANAGER
    if (isCPMApprover) {
      const soNumber = submissionDetail?.SONumber
      if (!soNumber || String(soNumber).trim() === '') {
        toast.error('SO# / SAP PR# must be filled before approving')
        return
      }
    }

    const formattedSubmissionDetail = normalizeGCPRSubmissionForApi(submissionDetail, {
      includeFilledBuyNow: true,
    })

    dispatch(
      showApprovalRemarkModal({
        approvalType: OfferApprovalType.GCPR_APPROVE,
        offerId: offerId,
        remark: '',
        updatePath: '',
        meta: {
          submissionIndex: submissionIndex,
          submissionDetail: formattedSubmissionDetail,
        },
      })
    )
  }
  const handleRejectGCMaterialPR = async () => {
    dispatch(
      showApprovalRemarkModal({
        approvalType: OfferApprovalType.GCPR_REJECT,
        offerId: offerId,
        remark: '',
        updatePath: '',
        meta: {
          submissionIndex: submissionIndex,
        },
      })
    )
  }

  const handleReplyGCPRInvitation = (slot) => {
    const { approverIndex, detail: inviterDetail } = slot

    dispatch(setApprovalInvitedUsers(inviterDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId,
            submissionIndex,
            approverIndex,
          },
          approvalRole: inviterDetail?.type,
          canManageInvitations: inviteSlots.length > 0,
          tableType: APPROVAL_TABLE_TYPE.GCPR_APPROVAL,
        },
      })
    )
  }

  const handleResubmitGCMaterialPR = async () => {
    try {
      const currentOffer = getValues()
      const formattedOffer = formatOffer(currentOffer)

      dispatch(showLoadingOverlay())

      await updateOffer.mutateAsync({
        id: currentOffer._id,
        data: formattedOffer,
      })

      await resubmitGCMaterialPR.mutateAsync({
        offerId: currentOffer._id,
        submissionIndex: submissionIndex,
      })
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleArchiveGCMaterialPR = async () => {
    try {
      await archiveGCMaterialPR.mutateAsync({
        offerId,
        submissionIndex,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleOpenGCPRInvitationDetailModal = (slot) => {
    const { approverIndex, detail: approverDetail } = slot

    dispatch(setApprovalInvitedUsers(approverDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId,
            submissionIndex,
            approverIndex,
          },
          approvalRole: approverDetail?.type,
          canManageInvitations: canDoGCPRApproval,
          tableType: APPROVAL_TABLE_TYPE.GCPR_APPROVAL,
        },
      })
    )
  }

  return (
    <Flex direction={'column'} gap="2" w="100%">
      <GCPRSubmissionHeader
        submissionIndex={submissionIndex}
        submissionDetail={submissionDetail}
        canEditGCPRSubmission={canEditGCPRSubmission}
      />
      <Box overflowX={'auto'} mb="5" w="100%">
        <ClientSideTable
          textAlign={'center'}
          columnDef={columnDef}
          data={PRSubmissionTableData}
          disabled={false}
          updateData={handleUpdateTableData}
          fixed
        />
      </Box>

      <GCPRSubmissionFormFields
        submissionIndex={submissionIndex}
        canEditGCPRSubmission={canEditGCPRSubmission}
        miniumDeliveryDate={miniumDeliveryDate}
        showCSIDFields={showCSIDFields}
      />

      <GCPRApproverTable submissionIndex={submissionIndex} canEditPR={canEditGCPRSubmission} />
      <GCPRSubmissionActionButtons
        canEditGCPRSubmission={canEditGCPRSubmission}
        replySlots={canReplyGCPRInvitation ? replySlots : []}
        submissionStatus={submissionDetail.status}
        onSubmit={handleSubmitGCMaterialPR}
        onDeleteDraft={handleDeleteDraft}
        onResubmit={handleResubmitGCMaterialPR}
        onArchive={handleArchiveGCMaterialPR}
        onSelectReply={handleReplyGCPRInvitation}
        canDoGCPRApproval={canDoGCPRApproval}
        submissionContainActionRequiredApproverDetail={submissionContainActionRequiredApproverDetail}
        onApprove={handleApproveGCMaterialPR}
        onReject={handleRejectGCMaterialPR}
        showDeleteDraftInstead={isNullish(submissionDetail?._id)}
        inviteSlots={inviteSlots}
        onSelectInvite={handleOpenGCPRInvitationDetailModal}
      />
    </Flex>
  )
}

export default GCPRSubmission
